// Confirmation dialog for cancelling an in-progress capture session.
//
// Resolves bug #24 (operational gap: no recovery path when a field
// staffer accidentally opens a session on the wrong building/site).
// Cancel is destructive — the session row is deleted, the matching
// capture_items cascade away, AND any rooms the surveyor added during
// the cancelled session are removed (orphan-spaces cleanup landed
// alongside this PR). The building (if any) reverts to 'not_started'
// unless a prior surveyor's completed session for the same building
// still exists. Rooms with capture_items from that prior completed
// session are preserved by the server-side NOT EXISTS guard.
//
// UX: the dialog is opened from the capture screen's sticky bottom bar
// or hero overflow. Submit only fires `onConfirm` when the user clicks
// the destructive button — overlay click + Esc close without
// submitting (matches Dialog's default semantics for non-destructive
// dialogs, but the destructive button copy is unambiguous).

import { type ReactElement } from 'react'

import { Alert } from '../../ui/alert'
import { Button } from '../../ui/button'
import { Dialog } from '../../ui/dialog'

export interface CancelSessionDialogProps {
  /** True when the dialog should render. */
  open: boolean
  /** Display name for the building or site being cancelled. */
  scopeName: string
  /** 'building' shows building-specific copy; 'site' for site-level capture. */
  scope: 'building' | 'site'
  /** True while the cancel mutation is in flight. */
  submitting: boolean
  /** Error message to surface; null hides the alert row. */
  error: string | null
  /** Called when the user dismisses without confirming. */
  onClose: () => void
  /** Called when the user clicks the destructive confirm button. */
  onConfirm: () => void
}

export function CancelSessionDialog({
  open,
  scopeName,
  scope,
  submitting,
  error,
  onClose,
  onConfirm,
}: CancelSessionDialogProps): ReactElement {
  const title = 'Cancel capture session?'
  const description =
    scope === 'building'
      ? `This discards the in-progress capture for ${scopeName} and resets the building to Not started. Captured asset counts AND rooms added during this session will be deleted.`
      : `This discards the in-progress site-level capture for ${scopeName}. Captured site infrastructure counts for this session will be deleted.`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      dismissOnOverlayClick={!submitting}
    >
      <div className="flex flex-col gap-3" data-testid="cancel-session-dialog">
        {error !== null ? (
          <Alert variant="error" title="Could not cancel">
            {error}
          </Alert>
        ) : null}
        <p className="text-sm text-gray-700">
          This action cannot be undone. If you want to keep your captures
          and pause for later, close this dialog and use the back button
          instead.
        </p>
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            aria-label="Keep capture session"
          >
            Keep capturing
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={submitting}
            aria-label="Confirm cancel capture session"
            data-testid="confirm-cancel-session-btn"
          >
            {submitting ? 'Cancelling…' : 'Cancel session'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
